/**
 * landing/recent-guides — 최근 본 가이드 island.
 *
 * Mount (landing): `<section data-mount="recent-guides" hidden>` — read LocalStorage
 *   → 0 entries → section hidden 유지. 1+ → unhide + render.
 *
 * Mount (guide page): `<div data-mount="recent-guide-push" data-service-slug
 *   data-guide-slug data-guide-title hidden>` — 마운트 시 1 회 push.
 *
 * LocalStorage: `justleave.recent_guides` (max 5 array).
 *   각 entry = {service_slug, guide_slug, title, last_result, ts}
 *   last_result ∈ "success" | "partial" | "fail" | null
 *     — guide/feedback 의 castVote 성공 시 updateLastResult() 갱신.
 *
 * 익명 원칙 — 서버 동기화 0, 디바이스 local 만 (PRD §4.3).
 * 비공개 모드 / cookie 차단 → localStorage throw 시 catch → silent skip.
 *
 * bfcache 동기화 — 가이드 페이지에서 back 으로 랜딩 복귀 시 브라우저가
 *   DOM 캐시 복원 (script 재실행 X). `pageshow` listener 가 LocalStorage 재독
 *   + re-render 로 최신 last_result 즉시 반영 (새로고침 불필요).
 */

const STORAGE_KEY = "justleave.recent_guides";
const MAX_ENTRIES = 5;

export type Result = "success" | "partial" | "fail";

export type RecentGuideEntry = {
  service_slug: string;
  guide_slug: string;
  title: string;
  last_result: Result | null;
  ts: number;
};

function isValidEntry(x: unknown): x is RecentGuideEntry {
  if (!x || typeof x !== "object") return false;
  const e = x as Record<string, unknown>;
  if (typeof e.service_slug !== "string" || e.service_slug.length === 0) return false;
  if (typeof e.guide_slug !== "string" || e.guide_slug.length === 0) return false;
  if (typeof e.title !== "string" || e.title.length === 0) return false;
  if (typeof e.ts !== "number") return false;
  if (
    e.last_result !== null &&
    e.last_result !== "success" &&
    e.last_result !== "partial" &&
    e.last_result !== "fail"
  ) {
    return false;
  }
  return true;
}

function readEntries(): RecentGuideEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isValidEntry).slice(0, MAX_ENTRIES);
  } catch {
    return [];
  }
}

function writeEntries(entries: RecentGuideEntry[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(0, MAX_ENTRIES)));
  } catch {
    // 비공개 모드 / quota 초과 — silent skip (정책 침범 X).
  }
}

export function pushRecentGuide(input: {
  service_slug: string;
  guide_slug: string;
  title: string;
}): void {
  const existing = readEntries();
  const old = existing.find(
    (e) => e.service_slug === input.service_slug && e.guide_slug === input.guide_slug,
  );
  const filtered = existing.filter(
    (e) => !(e.service_slug === input.service_slug && e.guide_slug === input.guide_slug),
  );
  const next: RecentGuideEntry = {
    service_slug: input.service_slug,
    guide_slug: input.guide_slug,
    title: input.title,
    last_result: old?.last_result ?? null,
    ts: Date.now(),
  };
  writeEntries([next, ...filtered]);
}

export function updateLastResult(
  service_slug: string,
  guide_slug: string,
  last_result: Result,
): void {
  const existing = readEntries();
  let updated = false;
  const next = existing.map((e) => {
    if (e.service_slug === service_slug && e.guide_slug === guide_slug) {
      updated = true;
      return { ...e, last_result };
    }
    return e;
  });
  if (updated) writeEntries(next);
}

type ResultTheme = {
  card: string;
  pill: string;
  ko: string;
  icon: string;
};

// 색깔 차별화 — last_result 별 left accent bar (3px) + bg tint + status pill.
// 카드 base = subtle elevation (shadow-sm) + hover:shadow-md + lift.
// 좌측 accent bar 가 시각 진입에서 즉답 (모서리 dot 보다 명료).
const RESULT_THEME: Record<Result, ResultTheme> = {
  success: {
    card: "bg-emerald-50 border-l-emerald-500",
    pill: "bg-emerald-100 text-emerald-800 ring-emerald-600/20",
    ko: "성공",
    icon: "✓",
  },
  partial: {
    card: "bg-amber-50 border-l-amber-500",
    pill: "bg-amber-100 text-amber-800 ring-amber-600/20",
    ko: "부분",
    icon: "~",
  },
  fail: {
    card: "bg-rose-50 border-l-rose-500",
    pill: "bg-rose-100 text-rose-800 ring-rose-600/20",
    ko: "실패",
    icon: "✕",
  },
};

const NEUTRAL_CARD = "bg-white border-l-slate-300";
// CARD_BASE: 1px 외곽 + 3px 좌측 accent + p-4 + shadow-sm 기본 + hover lift/shadow.
const CARD_BASE =
  "group relative block rounded-lg border border-l-[3px] border-slate-200 p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md";

function renderEntry(entry: RecentGuideEntry): HTMLLIElement {
  const li = document.createElement("li");
  const a = document.createElement("a");
  a.href = `/g/${entry.service_slug}/${entry.guide_slug}/`;
  a.dataset.l05Card = "";

  const theme = entry.last_result ? RESULT_THEME[entry.last_result] : null;
  a.className = `${CARD_BASE} ${theme ? theme.card : NEUTRAL_CARD}`;
  if (entry.last_result) a.dataset.l05Result = entry.last_result;

  // Title — text-sm 가독성 + line-clamp-2.
  const titleEl = document.createElement("p");
  titleEl.className = "line-clamp-2 text-sm font-semibold text-slate-900";
  titleEl.textContent = entry.title;
  a.appendChild(titleEl);

  if (theme) {
    // Status pill — rounded chip + ring (modern UI label 톤).
    const pill = document.createElement("span");
    pill.className = `mt-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ${theme.pill}`;
    pill.dataset.l05ResultLabel = "";
    pill.setAttribute("aria-label", `직전 결과: ${theme.ko}`);
    pill.textContent = `${theme.icon} ${theme.ko}`;
    a.appendChild(pill);
  }

  li.appendChild(a);
  return li;
}

export function mountRecentGuidePush(root: HTMLElement): void {
  const service_slug = root.dataset.serviceSlug;
  const guide_slug = root.dataset.guideSlug;
  const title = root.dataset.guideTitle;
  if (!service_slug || !guide_slug || !title) return;
  pushRecentGuide({ service_slug, guide_slug, title });
}

function renderRecentGuides(root: HTMLElement): void {
  const list = root.querySelector<HTMLUListElement>("ul");
  if (!list) return;
  const entries = readEntries();
  if (entries.length === 0) {
    root.hidden = true;
    return;
  }
  list.innerHTML = "";
  for (const entry of entries) {
    list.appendChild(renderEntry(entry));
  }
  root.hidden = false;
}

export function mountRecentGuides(root: HTMLElement): void {
  renderRecentGuides(root);
  // bfcache: 가이드 → back 시 LocalStorage 변경 즉시 반영. dataset.l05Bound 로 1회만.
  if (root.dataset.l05Bound === "1") return;
  root.dataset.l05Bound = "1";
  window.addEventListener("pageshow", () => renderRecentGuides(root));
}
